import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import './LegalPage.css';

const sections = [
  { id: 's1',  label: 'What Information Do We Collect?' },
  { id: 's2',  label: 'How Do We Use Your Information?' },
  { id: 's3',  label: 'Will Your Information Be Shared?' },
  { id: 's4',  label: 'Who Will Your Information Be Shared With?' },
  { id: 's5',  label: 'How Do We Handle Social Logins?' },
  { id: 's6',  label: 'Third-Party Websites' },
  { id: 's7',  label: 'How Long Do We Keep Your Information?' },
  { id: 's8',  label: 'How Do We Keep Your Information Safe?' },
  { id: 's9',  label: 'What Are Your Privacy Rights?' },
  { id: 's10', label: 'Do-Not-Track Features' },
  { id: 's11', label: 'California Residents' },
  { id: 's12', label: 'Updates to This Notice' },
  { id: 's13', label: 'Contact Us' },
  { id: 's14', label: 'Review, Update or Delete Your Data' },
];

export default function PrivacyPolicy() {
  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = 'Privacy Policy — PictureIt';
  }, []);

  return (
    <div className="legal-page">
      {/* Hero */}
      <div className="legal-hero">
        <div className="legal-hero-badge">Legal</div>
        <h1>Privacy Policy</h1>
        <div className="legal-hero-meta">
          <span>Last updated: December 19, 2020</span>
          <span>PictureIt · pictureitteam@gmail.com</span>
        </div>
      </div>

      <div className="legal-layout">
        {/* Sidebar */}
        <aside className="legal-sidebar">
          <h3>On This Page</h3>
          <nav>
            {sections.map((s) => (
              <a key={s.id} href={`#${s.id}`}>{s.label}</a>
            ))}
          </nav>
          <Link to="/" className="legal-sidebar-back">← Back to Home</Link>
        </aside>

        {/* Main content */}
        <main className="legal-content">
          <p className="legal-intro">
            Thank you for choosing to be part of our community at <strong>PictureIt</strong>. We are
            committed to protecting your personal information and your right to privacy. If you have any
            questions or concerns, please contact us at{' '}
            <a href="mailto:pictureitteam@gmail.com">pictureitteam@gmail.com</a>.
          </p>

          {/* Table of Contents */}
          <div className="legal-toc">
            <h3>Table of Contents</h3>
            <ol>
              {sections.map((s, i) => (
                <li key={s.id}>
                  <a href={`#${s.id}`}>{i + 1}. {s.label}</a>
                </li>
              ))}
            </ol>
          </div>

          {/* Section 1 */}
          <div className="legal-section" id="s1">
            <div className="legal-section-header">
              <span className="legal-section-number">1</span>
              <h2>What Information Do We Collect?</h2>
            </div>
            <h3>Personal information you disclose to us</h3>
            <p>
              We collect personal information that you voluntarily provide to us when you register on the App,
              express an interest in obtaining information about us or our products and Services, when you
              participate in activities on the App, or otherwise when you contact us.
            </p>
            <p>The personal information we collect may include: names, email addresses, and other similar information.</p>
            <h3>Social Media Login Data</h3>
            <p>
              We may provide you with the option to register using your existing social media account details
              (Facebook, Twitter, etc.). If you choose to register this way, we will collect the information described
              in the "Social Logins" section below.
            </p>
            <h3>Information automatically collected</h3>
            <p>
              We automatically collect certain information when you visit, use, or navigate the App. This includes
              device and usage information such as your IP address, browser and device characteristics, operating
              system, language preferences, referring URLs, and other technical information.
            </p>
            <ul>
              <li><strong>Log and Usage Data</strong> — IP address, device info, browser type, activity timestamps, pages/files viewed, searches, and error reports.</li>
              <li><strong>Device Data</strong> — Computer, phone, tablet, or other device information including hardware model, OS, ISP/mobile carrier.</li>
            </ul>
            <h3>Information collected through our App</h3>
            <ul>
              <li><strong>Mobile Device Access</strong> — We may request access to your device's contacts, camera, and other features. You can change permissions in your device settings.</li>
              <li><strong>Push Notifications</strong> — We may request to send push notifications. You may opt out in your device settings.</li>
            </ul>
            <h3>Information collected from other sources</h3>
            <p>
              We may obtain information from public databases, marketing partners, social media platforms, and
              other outside sources to enhance our ability to provide relevant services to you.
            </p>
          </div>

          <div className="legal-divider" />

          {/* Section 2 */}
          <div className="legal-section" id="s2">
            <div className="legal-section-header">
              <span className="legal-section-number">2</span>
              <h2>How Do We Use Your Information?</h2>
            </div>
            <p>We use personal information collected via our App for a variety of business purposes:</p>
            <ul>
              <li>To facilitate account creation and logon process</li>
              <li>To post testimonials (with your consent)</li>
              <li>To request feedback and contact you about your use of our App</li>
              <li>To enable user-to-user communications</li>
              <li>To manage user accounts</li>
              <li>To send administrative information, product updates, and policy change notices</li>
              <li>To protect our Services from fraud and abuse</li>
              <li>To enforce our terms, conditions, and policies</li>
              <li>To respond to legal requests and prevent harm</li>
              <li>To send marketing and promotional communications (you may opt out at any time)</li>
              <li>To deliver targeted advertising tailored to your interests</li>
            </ul>
          </div>

          <div className="legal-divider" />

          {/* Section 3 */}
          <div className="legal-section" id="s3">
            <div className="legal-section-header">
              <span className="legal-section-number">3</span>
              <h2>Will Your Information Be Shared With Anyone?</h2>
            </div>
            <p>We only share information with your consent, to comply with laws, to provide you with services, to protect your rights, or to fulfill business obligations. Legal bases include:</p>
            <ul>
              <li><strong>Consent</strong> — when you have given specific consent for a specific purpose.</li>
              <li><strong>Legitimate Interests</strong> — when reasonably necessary to achieve our legitimate business interests.</li>
              <li><strong>Performance of a Contract</strong> — to fulfill the terms of a contract with you.</li>
              <li><strong>Legal Obligations</strong> — when required by applicable law, court order, or governmental request.</li>
              <li><strong>Vital Interests</strong> — when necessary to investigate, prevent, or take action regarding potential violations, fraud, or safety threats.</li>
            </ul>
            <p>We may also share information in connection with business transfers (mergers, acquisitions, etc.) and with vendors, consultants, and third-party service providers who perform services on our behalf.</p>
          </div>

          <div className="legal-divider" />

          {/* Section 4 */}
          <div className="legal-section" id="s4">
            <div className="legal-section-header">
              <span className="legal-section-number">4</span>
              <h2>Who Will Your Information Be Shared With?</h2>
            </div>
            <p>We only share and disclose your information with the following third parties:</p>
            <ul>
              <li><strong>Advertising &amp; Lead Generation</strong> — Google AdSense</li>
              <li><strong>Affiliate Marketing</strong> — Amazon Affiliation</li>
              <li><strong>Third-Party Account Connections</strong> — Google, Facebook, Apple</li>
              <li><strong>Cloud Computing</strong> — Amazon Web Services (AWS), Google Cloud Platform</li>
              <li><strong>Authentication</strong> — Facebook Login, Google Sign-In, Apple Sign In</li>
              <li><strong>Analytics</strong> — Google Analytics</li>
              <li><strong>Testing</strong> — TestFlight</li>
            </ul>
          </div>

          <div className="legal-divider" />

          {/* Section 5 */}
          <div className="legal-section" id="s5">
            <div className="legal-section-header">
              <span className="legal-section-number">5</span>
              <h2>How Do We Handle Your Social Logins?</h2>
            </div>
            <p>
              Our App offers you the ability to register and login using your third-party social media account
              details (Facebook, Twitter, etc.). Where you choose to do this, we will receive certain profile
              information from your social media provider — typically your name, email address, friends list, and
              profile picture.
            </p>
            <p>
              We will use the information we receive only for the purposes described in this privacy notice. We
              are not responsible for other uses of your personal information by your third-party social media
              provider and recommend you review their privacy settings.
            </p>
          </div>

          <div className="legal-divider" />

          {/* Section 6 */}
          <div className="legal-section" id="s6">
            <div className="legal-section-header">
              <span className="legal-section-number">6</span>
              <h2>What Is Our Stance on Third-Party Websites?</h2>
            </div>
            <p>
              The App may contain advertisements from third parties that are not affiliated with us and which may
              link to other websites, online services, or mobile applications. We cannot guarantee the safety and
              privacy of data you provide to any third parties, and their data collection is not covered by this
              privacy notice. We recommend reviewing the policies of any third parties before sharing your data.
            </p>
          </div>

          <div className="legal-divider" />

          {/* Section 7 */}
          <div className="legal-section" id="s7">
            <div className="legal-section-header">
              <span className="legal-section-number">7</span>
              <h2>How Long Do We Keep Your Information?</h2>
            </div>
            <p>
              We will only keep your personal information for as long as it is necessary for the purposes set out
              in this privacy notice, unless a longer retention period is required by law. When we have no ongoing
              legitimate business need to process your personal information, we will delete or anonymize it, or
              securely store it and isolate it from further processing until deletion is possible.
            </p>
          </div>

          <div className="legal-divider" />

          {/* Section 8 */}
          <div className="legal-section" id="s8">
            <div className="legal-section-header">
              <span className="legal-section-number">8</span>
              <h2>How Do We Keep Your Information Safe?</h2>
            </div>
            <p>
              We have implemented appropriate technical and organizational security measures designed to protect
              the security of any personal information we process. However, no electronic transmission over the
              Internet or information storage technology can be guaranteed to be 100% secure. Transmission of
              personal information to and from our App is at your own risk. You should only access the App within
              a secure environment.
            </p>
          </div>

          <div className="legal-divider" />

          {/* Section 9 */}
          <div className="legal-section" id="s9">
            <div className="legal-section-header">
              <span className="legal-section-number">9</span>
              <h2>What Are Your Privacy Rights?</h2>
            </div>
            <p>
              You may review, change, or terminate your account at any time by logging into your account settings.
              If you are a resident in the European Economic Area and believe we are unlawfully processing your
              personal information, you also have the right to complain to your local data protection supervisory
              authority.
            </p>
            <h3>Opting out of email marketing</h3>
            <p>
              You can unsubscribe from our marketing email list at any time by clicking the unsubscribe link in
              any email we send, or by contacting us. You may still receive service-related emails necessary for
              account administration.
            </p>
          </div>

          <div className="legal-divider" />

          {/* Section 10 */}
          <div className="legal-section" id="s10">
            <div className="legal-section-header">
              <span className="legal-section-number">10</span>
              <h2>Controls for Do-Not-Track Features</h2>
            </div>
            <p>
              Most web browsers and some mobile operating systems include a Do-Not-Track ("DNT") feature.
              No uniform technology standard for recognizing and implementing DNT signals has been finalized.
              As such, we do not currently respond to DNT browser signals. If a standard is adopted that we must
              follow in the future, we will inform you in a revised version of this notice.
            </p>
          </div>

          <div className="legal-divider" />

          {/* Section 11 */}
          <div className="legal-section" id="s11">
            <div className="legal-section-header">
              <span className="legal-section-number">11</span>
              <h2>Do California Residents Have Specific Privacy Rights?</h2>
            </div>
            <p>
              <strong>Yes.</strong> California Civil Code Section 1798.83 ("Shine The Light" law) permits California
              residents to request information about categories of personal information disclosed to third parties for
              direct marketing purposes. To make such a request, contact us using the information below.
            </p>
            <p>
              If you are under 18 years of age, reside in California, and have a registered account, you have the
              right to request removal of unwanted data you publicly posted on the App.
            </p>
          </div>

          <div className="legal-divider" />

          {/* Section 12 */}
          <div className="legal-section" id="s12">
            <div className="legal-section-header">
              <span className="legal-section-number">12</span>
              <h2>Do We Make Updates to This Notice?</h2>
            </div>
            <p>
              Yes, we will update this notice as necessary to stay compliant with relevant laws. The updated
              version will be indicated by an updated "Revised" date. If we make material changes, we may notify
              you by posting a notice prominently or sending you a notification directly. We encourage you to
              review this privacy notice frequently.
            </p>
          </div>

          <div className="legal-divider" />

          {/* Section 13 */}
          <div className="legal-section" id="s13">
            <div className="legal-section-header">
              <span className="legal-section-number">13</span>
              <h2>How Can You Contact Us About This Notice?</h2>
            </div>
            <div className="legal-contact-box">
              <p>If you have questions or comments about this notice, please reach out:</p>
              <a href="mailto:pictureitteam@gmail.com">pictureitteam@gmail.com</a>
              <p style={{ marginTop: '10px' }}>PictureIt</p>
            </div>
          </div>

          <div className="legal-divider" />

          {/* Section 14 */}
          <div className="legal-section" id="s14">
            <div className="legal-section-header">
              <span className="legal-section-number">14</span>
              <h2>How Can You Review, Update, or Delete the Data We Collect From You?</h2>
            </div>
            <p>
              Based on the applicable laws of your country, you may have the right to request access to the
              personal information we collect from you, change that information, or delete it in some
              circumstances. To request to review, update, or delete your personal information, please email us
              at <a href="mailto:pictureitteam@gmail.com">pictureitteam@gmail.com</a>. We will respond to
              your request within <strong>30 days</strong>.
            </p>
          </div>
        </main>
      </div>

      <div className="legal-footer-strip">
        <span>© {new Date().getFullYear()} PictureIt. All rights reserved.</span>
        <Link to="/privacy-policy">Privacy Policy</Link>
        <Link to="/terms-of-service">Terms of Service</Link>
        <Link to="/">Home</Link>
      </div>
    </div>
  );
}
